import { USER_DAC7_ACTION_TYPE } from 'store/action-types/user-dac7.action-types';
import { USER_PROMO_CODE_ACTION } from 'store/action-types/user-promo-code-service.action-types';
import { likeToggledLocally } from 'store/actions/like-service.actions';
import {
  Dac7FormErrorType,
  DAC7FormRequiredFields,
  IBusinessEntityData,
  IPrivatePersonData,
  PrivatePersonDac7FormField,
  UserAccountType,
  UserType,
} from 'types/user.types';
import { IUserPromoCode } from 'types/user-promo-code.types';

import {
  CONTACT_SET,
  USER_ADDRESS_UPDATED,
  USER_CONTACT_DETAILS_CONFIRMED_PROMPT,
  USER_EMAIL_CONFIRMED_SET,
  USER_LOGIN_LOADING_SET,
  USER_MARKETING_EMAIL_OPT_IN_PROMPT,
  USER_MARKETING_EMAIL_OPT_IN_SET,
  USER_REMOVE,
  USER_SET,
  YAGA_UUID,
} from '../action-types/user.action-types';

export type UserReducer = {
  user: UserType | Record<string, any>;
  isLoading: boolean;
  marketingEmailOptPrompted: boolean;
  contactDetailsConfirmationPrompted: boolean;
  marketingEmailOptedStatus?: boolean;
  emailConfirmed: boolean;
  userPromoCodes: IUserPromoCode[];
  userDac7Form: {
    isValid: Record<UserAccountType, boolean>;
    isTouched: Record<UserAccountType, boolean>;
    accountType: UserAccountType | '';
    data: Record<UserAccountType, IPrivatePersonData | IBusinessEntityData>;
    errors: Record<UserAccountType, Dac7FormErrorType>;
    isSubmitted: boolean;
  };
  userDac7FormConfirmation: {
    isOpen: boolean;
  };
  yagaUUID: string;
};

const initialState: UserReducer = {
  user: {},
  isLoading: true,
  marketingEmailOptPrompted: null,
  contactDetailsConfirmationPrompted: false,
  marketingEmailOptedStatus: null,
  emailConfirmed: null,
  userPromoCodes: [],
  userDac7Form: {
    isSubmitted: false,
    isValid: {
      [UserAccountType.PRIVATE_PERSON]: false,
      [UserAccountType.BUSINESS]: false,
    },
    isTouched: {
      [UserAccountType.PRIVATE_PERSON]: false,
      [UserAccountType.BUSINESS]: false,
    },
    accountType: '',
    data: {
      [UserAccountType.PRIVATE_PERSON]: {
        fullName: '',
        dateOfBirth: '',
        residencyAddress: '',
        residencyCountry: 'EE',
        idCode: '',
        vat: '',
      },
      [UserAccountType.BUSINESS]: {
        name: '',
        registrationAddress: '',
        registryCode: '',
        operatingCountry: 'EE',
        vat: '',
      },
    },
    errors: {
      [UserAccountType.PRIVATE_PERSON]: null,
      [UserAccountType.BUSINESS]: null,
    },
  },
  userDac7FormConfirmation: {
    isOpen: false,
  },
  yagaUUID: '',
};

const userReducer = (state = initialState, action) => {
  switch (action.type) {
    case USER_SET: {
      return {
        ...state,
        user: {
          ...action.data,
          isLoggedIn: action.data?.isLoggedIn ?? state.user.isLoggedIn,
        },
      };
    }
    case USER_LOGIN_LOADING_SET: {
      return {
        ...state,
        isLoading: action.data,
      };
    }
    case USER_CONTACT_DETAILS_CONFIRMED_PROMPT: {
      return {
        ...state,
        contactDetailsConfirmationPrompted: action.data,
      };
    }
    case USER_EMAIL_CONFIRMED_SET: {
      return {
        ...state,
        emailConfirmed: action.data,
      };
    }
    case USER_MARKETING_EMAIL_OPT_IN_PROMPT: {
      return {
        ...state,
        marketingEmailOptInPrompted: action.data,
      };
    }
    case USER_MARKETING_EMAIL_OPT_IN_SET: {
      return {
        ...state,
        marketingEmailOptedStatus: action.data,
      };
    }
    case CONTACT_SET: {
      return {
        ...state,
        contact: action.data,
      };
    }
    case USER_REMOVE: {
      return {
        ...initialState,
        isLoading: false,
      };
    }
    case likeToggledLocally.type: {
      const likedData: { id: number; liked: boolean } = action.payload;
      const likedProducts = Object.assign({}, state.user.likedProducts);

      if (likedData.liked) {
        likedProducts[likedData.id] = true;
      } else if (likedData.id in likedProducts) {
        delete likedProducts[likedData.id];
      }

      return {
        ...state,
        user: {
          ...state.user,
          likedProducts,
        },
      };
    }
    case USER_PROMO_CODE_ACTION.SET_USER_PROMO_CODES: {
      return {
        ...state,
        userPromoCodes: action.payload,
      };
    }
    case USER_DAC7_ACTION_TYPE.SET_ACCOUNT_TYPE: {
      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          accountType: action.payload,
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.SET_INFORMATION: {
      const { field, value } = action.payload;
      const { accountType, data } = state.userDac7Form;

      const changedFormDataState: IPrivatePersonData | IBusinessEntityData = {
        ...data[accountType],
        [field]: value,
      };

      let allFieldsValid = true;

      for (const field in changedFormDataState) {
        const isEstoniaSelected = data[UserAccountType.PRIVATE_PERSON][PrivatePersonDac7FormField.residencyCountry] === 'EE';

        if (DAC7FormRequiredFields[accountType]?.includes(field) && !changedFormDataState[field]) {
          allFieldsValid = false;
        }

        // idCode is required field, if user selects Estonia
        if (accountType === UserAccountType.PRIVATE_PERSON) {
          if (
            !changedFormDataState[field] &&
            isEstoniaSelected &&
            field === PrivatePersonDac7FormField.idCode &&
            !changedFormDataState[PrivatePersonDac7FormField.idCode]
          ) {
            allFieldsValid = false;
          }

          // it is required to have at least one of the fields filled, if user selects any country other then Estonia
          if (
            !isEstoniaSelected &&
            !changedFormDataState[field] &&
            ((field === PrivatePersonDac7FormField.idCode && !changedFormDataState[PrivatePersonDac7FormField.vat]) ||
              (!isEstoniaSelected && field === PrivatePersonDac7FormField.vat && !changedFormDataState[PrivatePersonDac7FormField.idCode]))
          ) {
            allFieldsValid = false;
          }
        }
      }

      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          isValid: {
            ...state.userDac7Form.isValid,
            [accountType]: allFieldsValid,
          },
          isTouched: {
            ...state.userDac7Form.isTouched,
            [accountType]: true,
          },
          data: {
            ...state.userDac7Form.data,
            [accountType]: changedFormDataState,
          },
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.SET_REQUIRED_ERROR: {
      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          errors: {
            ...state.userDac7Form.errors,
            [state.userDac7Form.accountType]: {
              ...state.userDac7Form.errors[state.userDac7Form.accountType],
              [action.payload]: 'pages.DAC7.form.errors.required-field',
            },
          },
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.SET_CONDITIONALLY_REQUIRED_ERROR: {
      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          errors: {
            ...state.userDac7Form.errors,
            [state.userDac7Form.accountType]: {
              ...state.userDac7Form.errors[state.userDac7Form.accountType],
              [PrivatePersonDac7FormField.idCode]: 'pages.DAC7.form.errors.at-least-one-is-required',
              [PrivatePersonDac7FormField.vat]: 'pages.DAC7.form.errors.at-least-one-is-required',
            },
          },
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.SET_INVALID_VALUE_ERROR: {
      if (action.payload === PrivatePersonDac7FormField.fullName) {
        return {
          ...state,
          userDac7Form: {
            ...state.userDac7Form,
            errors: {
              ...state.userDac7Form.errors,
              [state.userDac7Form.accountType]: {
                ...state.userDac7Form.errors[state.userDac7Form.accountType],
                [action.payload]: 'pages.DAC7.form.errors.invalid-full-name',
              },
            },
          },
        };
      }

      return state;
    }
    case USER_DAC7_ACTION_TYPE.CLEAR_ERROR: {
      const fieldName = action.payload;
      const { errors, accountType } = state.userDac7Form;

      if (!errors || !errors[accountType]) {
        return state;
      }

      if (
        state.userDac7Form.accountType === UserAccountType.PRIVATE_PERSON &&
        (fieldName === PrivatePersonDac7FormField.idCode || fieldName === PrivatePersonDac7FormField.vat)
      ) {
        delete errors[accountType][PrivatePersonDac7FormField.idCode];
        delete errors[accountType][PrivatePersonDac7FormField.vat];
      }

      if (errors[accountType][fieldName]) {
        delete errors[accountType][fieldName];
      }

      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          errors: {
            ...state.userDac7Form.errors,
            [state.userDac7Form.accountType]: errors[accountType],
          },
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.OPEN_CONFIRMATION: {
      return {
        ...state,
        userDac7FormConfirmation: {
          isOpen: true,
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.CANCEL_CONFIRMATION: {
      return {
        ...state,
        userDac7FormConfirmation: {
          isOpen: false,
        },
      };
    }
    case USER_DAC7_ACTION_TYPE.PREFILL_FORM_WITH_SUBMITTED_DATA: {
      const { dac7_information, dac7_filled } = state.user;

      if (dac7_filled && dac7_information && !state.userDac7Form.isSubmitted) {
        const { accountType, data } = dac7_information;

        return {
          ...state,
          userDac7Form: {
            ...state.userDac7Form,
            accountType,
            data: {
              ...state.userDac7Form.data,
              [accountType]: data,
            },
          },
        };
      }

      return state;
    }
    case USER_DAC7_ACTION_TYPE.SET_FORM_SUBMITTED: {
      return {
        ...state,
        userDac7Form: {
          ...state.userDac7Form,
          isSubmitted: true,
        },
        userDac7FormConfirmation: {
          isOpen: false,
        },
      };
    }
    case USER_ADDRESS_UPDATED: {
      return {
        ...state,
        user: {
          ...state.user,
          address: {
            ...state.user.address,
            ...action.data,
          },
        },
      };
    }
    case YAGA_UUID: {
      return {
        ...state,
        yagaUUID: action.data,
      };
    }
    default: {
      return state;
    }
  }
};

export default userReducer;
